import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// RESOURCE SERVICE — library resources (guidelines, reports, IEC, videos…)
// Public GET /resources (filtered) + /resources/{slug}; admin CRUD.
// ================================================================================

export type ResourceType =
  | 'GUIDELINE' | 'REPORT' | 'TRAINING' | 'INFOGRAPHIC'
  | 'VIDEO' | 'PUBLICATION' | 'NOTIFICATION' | 'MEDIA'
  | 'IEC_MATERIAL' | 'CAMPAIGN_TOOLKIT' | 'REPORTING_FORMAT' | 'OTHER'

export type ResourceAudience =
  | 'PATIENT' | 'FAMILY' | 'NIKSHAY_MITRA' | 'VOLUNTEER_NGO'
  | 'NGO' | 'VOLUNTEER'
  | 'CSR' | 'HEALTHCARE_WORKER' | 'RESEARCHER' | 'MEDIA' | 'GENERAL'

export type ResourceStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

// 8 audience-oriented library categories (the new information architecture).
export type ResourceCategory =
  | 'PATIENT_RESOURCES' | 'FAMILY_RESOURCES' | 'HEALTHCARE_PROFESSIONALS'
  | 'NGOS' | 'SCHOOLS_COLLEGES' | 'CORPORATES' | 'MEDIA' | 'CAMPAIGN_MATERIALS'

// 15 topic axes (what a resource is about), orthogonal to format/audience.
export type ResourceTopic =
  | 'SYMPTOMS' | 'DIAGNOSIS' | 'TREATMENT' | 'NUTRITION' | 'PREVENTION'
  | 'MDR_TB' | 'CHILDHOOD_TB' | 'PREGNANCY_TB' | 'SCHEMES_BENEFITS'
  | 'AWARENESS' | 'CAMPAIGN' | 'NIKSHAY_MITRA' | 'INFECTION_CONTROL'
  | 'MENTAL_HEALTH' | 'GENERAL'

export interface Resource {
  id: number
  title: string
  slug: string
  description?: string
  resourceType: ResourceType
  audience?: ResourceAudience
  category?: ResourceCategory
  topic?: ResourceTopic
  language?: string
  sourceOrg?: string
  fileUrl?: string
  externalUrl?: string
  fileType?: string
  fileSize?: string
  thumbnailUrl?: string
  isFeatured?: boolean
  status: ResourceStatus
  publishedAt?: string
  viewCount?: number
  downloadCount?: number
  shareCount?: number
  createdAt?: string
  updatedAt?: string
  /** Language actually served by the API — the requested one if a translation exists, else "en". */
  matchedLanguage?: string
  /** Languages this resource is available in; always includes "en" (the canonical row). */
  availableLanguages?: string[]
}

export interface ResourceRequest {
  title: string
  slug?: string
  description?: string
  resourceType: ResourceType
  audience?: ResourceAudience
  category?: ResourceCategory
  topic?: ResourceTopic
  language?: string
  sourceOrg?: string
  fileUrl?: string
  externalUrl?: string
  fileType?: string
  fileSize?: string
  thumbnailUrl?: string
  isFeatured?: boolean
  status?: ResourceStatus
}

/** A single facet bucket — {key, count} — from the /resources/facets endpoint. */
export interface FacetBucket { key: string; count: number }

export interface ResourceFacets {
  categories: FacetBucket[]
  topics: FacetBucket[]
  formats: FacetBucket[]
  audiences: FacetBucket[]
}

export interface ResourceStats {
  TOTAL: number
  DRAFT: number
  PUBLISHED: number
  ARCHIVED: number
}

// ---- public ----

export async function listResources(params: {
  type?: string
  audience?: string
  category?: string
  topic?: string
  language?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Resource>> {
  const qs = new URLSearchParams()
  if (params.type) qs.set('type', params.type)
  if (params.audience) qs.set('audience', params.audience)
  if (params.category) qs.set('category', params.category)
  if (params.topic) qs.set('topic', params.topic)
  if (params.language) qs.set('language', params.language)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 12))
  const res = await api.get<ApiResponse<PaginatedResponse<Resource>>>(`/resources?${qs.toString()}`)
  return res.data.data
}

/** Public: per category/topic/format/audience counts over published resources — powers the
 *  category tiles and the Language|Format|Audience|Topic filter bar. */
export async function getResourceFacets(): Promise<ResourceFacets> {
  const res = await api.get<ApiResponse<ResourceFacets>>('/resources/facets')
  return res.data.data
}

/** Public: record a social share of a resource (increments the share counter). No auth. */
export async function shareResource(id: number): Promise<number> {
  const res = await api.post<ApiResponse<{ shareCount: number }>>(`/resources/${id}/share`)
  return res.data.data.shareCount
}

export async function getResource(slug: string): Promise<Resource> {
  const res = await api.get<ApiResponse<Resource>>(`/resources/${slug}`)
  return res.data.data
}

// ---- saved / bookmarked resources (volunteer library) ----
// The backend's saved-list endpoint returns `ResourceResponse` (the admin DTO),
// whose field names differ from the public `Resource` (`resourceType` not `type`,
// `audience` not `resourceAudience`, plus viewCount/downloadCount/shareCount etc.).
// We expose it under its own type so callers stay honest about the shape.

export interface SavedResourceItem {
  id: number
  title: string
  slug: string
  description: string
  resourceType: ResourceType
  audience: ResourceAudience
  category: ResourceCategory
  topic: ResourceTopic
  language: string
  sourceOrg?: string
  fileUrl?: string
  externalUrl?: string
  fileType?: string
  fileSize?: string
  thumbnailUrl?: string
  isFeatured?: boolean
  status: ResourceStatus
  publishedAt?: string
  viewCount?: number
  downloadCount?: number
  shareCount?: number
  createdAt?: string
  updatedAt?: string
  matchedLanguage?: string
  availableLanguages?: string[]
}

/** Save (bookmark) a resource for the current volunteer. */
export async function saveResource(id: number): Promise<boolean> {
  const res = await api.post<ApiResponse<{ saved: boolean }>>(`/resources/${id}/save`)
  return res.data.data.saved
}

/** Remove a resource from the current volunteer's saved list. */
export async function unsaveResource(id: number): Promise<boolean> {
  const res = await api.delete<ApiResponse<{ saved: boolean }>>(`/resources/${id}/save`)
  return res.data.data.saved
}

/** Whether the current volunteer has saved a given resource. */
export async function isResourceSaved(id: number): Promise<boolean> {
  const res = await api.get<ApiResponse<{ saved: boolean }>>(`/resources/${id}/save/status`)
  return res.data.data.saved
}

/** The current volunteer's saved resources (paged). Unpublished rows are excluded server-side. */
export async function getSavedResources(page = 0, size = 20): Promise<PaginatedResponse<SavedResourceItem>> {
  const res = await api.get<ApiResponse<PaginatedResponse<SavedResourceItem>>>(
    `/resources/saved?page=${page}&size=${size}`)
  return res.data.data
}

// ---- admin ----

export async function listResourcesAdmin(params: {
  status?: string
  type?: string
  category?: string
  topic?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Resource>> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.type) qs.set('type', params.type)
  if (params.category) qs.set('category', params.category)
  if (params.topic) qs.set('topic', params.topic)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<Resource>>>(`/resources/admin?${qs.toString()}`)
  return res.data.data
}

export async function getResourceStats(): Promise<ResourceStats> {
  const res = await api.get<ApiResponse<ResourceStats>>(`/resources/stats`)
  return res.data.data
}

export async function createResource(req: ResourceRequest): Promise<Resource> {
  const res = await api.post<ApiResponse<Resource>>('/resources', req)
  return res.data.data
}

export async function updateResource(id: number, req: ResourceRequest): Promise<Resource> {
  const res = await api.put<ApiResponse<Resource>>(`/resources/${id}`, req)
  return res.data.data
}

export async function changeResourceStatus(id: number, status: ResourceStatus): Promise<Resource> {
  const res = await api.put<ApiResponse<Resource>>(`/resources/${id}/status?status=${status}`)
  return res.data.data
}

export async function deleteResource(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/resources/${id}`)
}

// ---- label maps ----

export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  GUIDELINE: 'Guidelines',
  REPORT: 'Reports',
  TRAINING: 'Training',
  INFOGRAPHIC: 'Infographics',
  VIDEO: 'Videos',
  PUBLICATION: 'Publications',
  NOTIFICATION: 'Notifications',
  MEDIA: 'Media assets',
  IEC_MATERIAL: 'IEC materials',
  CAMPAIGN_TOOLKIT: 'Campaign toolkit',
  REPORTING_FORMAT: 'Reporting formats',
  OTHER: 'Other',
}

export const AUDIENCE_LABELS: Record<ResourceAudience, string> = {
  PATIENT: 'Patient',
  FAMILY: 'Family',
  NIKSHAY_MITRA: 'TB Supporter (Ni-kshay Mitra)',
  VOLUNTEER_NGO: 'Volunteer / NGO',
  NGO: 'NGO',
  VOLUNTEER: 'Volunteer',
  CSR: 'CSR',
  HEALTHCARE_WORKER: 'Healthcare Worker',
  RESEARCHER: 'Researcher',
  MEDIA: 'Media',
  GENERAL: 'General',
}

export const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  hi: 'हिन्दी',
  mr: 'मराठी',
  multi: 'Multilingual',
}

// 8 audience-oriented library categories — the new IA. These are stable enum keys;
// human labels come from i18n (pages.resources.categories.<key>).
export const CATEGORY_KEYS: ResourceCategory[] = [
  'PATIENT_RESOURCES', 'FAMILY_RESOURCES', 'HEALTHCARE_PROFESSIONALS',
  'NGOS', 'SCHOOLS_COLLEGES', 'CORPORATES', 'MEDIA', 'CAMPAIGN_MATERIALS',
]

export const CATEGORY_LABELS: Record<ResourceCategory, string> = {
  PATIENT_RESOURCES: 'Patient Resources',
  FAMILY_RESOURCES: 'Family Resources',
  HEALTHCARE_PROFESSIONALS: 'Healthcare Professionals',
  NGOS: 'NGOs',
  SCHOOLS_COLLEGES: 'Schools & Colleges',
  CORPORATES: 'Corporates',
  MEDIA: 'Media',
  CAMPAIGN_MATERIALS: 'Campaign Materials',
}

export const TOPIC_KEYS: ResourceTopic[] = [
  'SYMPTOMS', 'DIAGNOSIS', 'TREATMENT', 'NUTRITION', 'PREVENTION',
  'MDR_TB', 'CHILDHOOD_TB', 'PREGNANCY_TB', 'SCHEMES_BENEFITS',
  'AWARENESS', 'CAMPAIGN', 'NIKSHAY_MITRA', 'INFECTION_CONTROL',
  'MENTAL_HEALTH', 'GENERAL',
]

export const TOPIC_LABELS: Record<ResourceTopic, string> = {
  SYMPTOMS: 'Symptoms',
  DIAGNOSIS: 'Diagnosis',
  TREATMENT: 'Treatment',
  NUTRITION: 'Nutrition',
  PREVENTION: 'Prevention',
  MDR_TB: 'Drug-resistant TB',
  CHILDHOOD_TB: 'Childhood TB',
  PREGNANCY_TB: 'TB in Pregnancy',
  SCHEMES_BENEFITS: 'Schemes & Benefits',
  AWARENESS: 'Awareness',
  CAMPAIGN: 'Campaign',
  NIKSHAY_MITRA: 'Ni-kshay Mitra',
  INFECTION_CONTROL: 'Infection Control',
  MENTAL_HEALTH: 'Mental Health',
  GENERAL: 'General',
}

export const RESOURCE_STATUS_LABELS: Record<ResourceStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
}